'use client'

import BurgerButton from '@/components/ui/BurgerButton'
import { LanguageToggle } from '@/components/ui/LanguageToggle'
import { TimeDisplay } from '@/components/ui/TimeDisplay'
import { Link, usePathname } from '@/i18n/routing'
import classNames from 'classnames'
import { motion } from 'framer-motion'
import { useTranslations } from 'next-intl'
import Image from 'next/image'
import { useEffect, useRef, useState } from 'react'
import MobileNavLink from './MobileNavLink'
import NavLink from './NavLink'

const navItems = [
  { href: '/', title: 'Home', exact: true },
  { href: '/ki' as const, title: 'KI' },
  { href: '/leistungen' as const, title: 'Leistungen' },
  { href: '/projekte' as const, title: 'Projekte' },
  { href: '/about' as const, title: 'Über uns' },
  { href: '/news' as const, title: 'News' },
  { href: '/kontakt' as const, title: 'Kontakt' }
] as const

export const NavBar = () => {
  const pathname = usePathname()
  const [isOpen, setIsOpen] = useState(false)
  const t = useTranslations('navigation')
  const [hoveredIdx, setHoveredIdx] = useState<number | null>(null)
  const [selectedIdx, setSelectedIdx] = useState<number | null>(null)
  const [highlightStyle, setHighlightStyle] = useState({})
  const itemRefs = useRef<(HTMLLIElement | null)[]>([])

  const activeIdx = navItems.findIndex((item) => {
    const exact = 'exact' in item && item.exact
    return exact ? pathname === item.href : pathname.startsWith(item.href)
  })

  useEffect(() => {
    const idx = hoveredIdx !== null ? hoveredIdx : activeIdx
    const el = itemRefs.current[idx]

    if (el) {
      setSelectedIdx(idx)
      const rect = el.getBoundingClientRect()
      const parentRect = el.parentElement?.getBoundingClientRect()
      setHighlightStyle({
        width: `${rect.width}px`,
        left: `${rect.left - (parentRect?.left ?? 0)}px`,
        top: `${rect.top - (parentRect?.top ?? 0)}px`,
        height: `${rect.height}px`,
        borderRadius: '999px'
      })
    }
  }, [activeIdx, hoveredIdx])

  useEffect(() => {
    setIsOpen(false)
  }, [pathname])

  return (
    <>
      <motion.nav
        variants={{
          hidden: { opacity: 0, y: -100 },
          enter: { opacity: 1, y: 0 }
        }}
        initial="hidden"
        exit="exit"
        animate="enter"
        transition={{ type: 'tween', ease: 'easeInOut', duration: 0.4 }}
        // Persistente Compositor-Ebene: ohne sie rastert der Browser den
        // backdrop-blur bei jedem Scroll-Frame neu (sichtbares Flackern),
        // sobald framer-motion nach der Einblendung transform:none setzt
        style={{ willChange: 'transform' }}
        className={classNames('max-w-[1440px] w-full mx-auto hidden lg:block z-100 bg-white/10 rounded-3xl bg-clip-padding backdrop-filter backdrop-blur-md shadow-md border border-white/20 p-8')}
      >
        <div className="flex flex-row items-center justify-between">
          <div className="flex flex-row items-center">
            <Link href="/">
              <Image src={'/images/logos/kommers-logo.svg'} width={50} height={50} className="mr-5 h-full w-auto max-w-none object-cover" alt="KOMMERS Logo" />
            </Link>
            <div className="w-2 h-2 mr-2 rounded-full bg-accent" />
            <p className="text-KMRSx24-GreyLight whitespace-nowrap">
              Fürth, Deutschland <TimeDisplay />
            </p>
          </div>
          <div className="flex flex-row items-center gap-x-6">
            <ul className="flex flex-row items-center gap-x-6 nav-list-liquid">
              <div className="nav-highlight-liquid" style={highlightStyle} aria-hidden="true" />
              <li
                ref={(el) => {
                  itemRefs.current[0] = el
                }}
                className="relative z-10 flex"
                onMouseEnter={() => setHoveredIdx(0)}
                onMouseLeave={() => setHoveredIdx(null)}
              >
                <NavLink href="/" title={t('home')} isActive={selectedIdx === 0} />
              </li>
              <li
                ref={(el) => {
                  itemRefs.current[1] = el
                }}
                className="relative z-10 flex"
                onMouseEnter={() => setHoveredIdx(1)}
                onMouseLeave={() => setHoveredIdx(null)}
              >
                <NavLink href="/ki" title={t('ai')} isActive={selectedIdx === 1} />
              </li>
              <li
                ref={(el) => {
                  itemRefs.current[2] = el
                }}
                className="relative z-10 flex"
                onMouseEnter={() => setHoveredIdx(2)}
                onMouseLeave={() => setHoveredIdx(null)}
              >
                <NavLink href="/leistungen" title={t('services')} isActive={selectedIdx === 2} />
              </li>
              <li
                ref={(el) => {
                  itemRefs.current[3] = el
                }}
                className="relative z-10 flex"
                onMouseEnter={() => setHoveredIdx(3)}
                onMouseLeave={() => setHoveredIdx(null)}
              >
                <NavLink href="/projekte" title={t('projects')} isActive={selectedIdx === 3} />
              </li>
              <li
                ref={(el) => {
                  itemRefs.current[4] = el
                }}
                className="relative z-10 flex"
                onMouseEnter={() => setHoveredIdx(4)}
                onMouseLeave={() => setHoveredIdx(null)}
              >
                <NavLink href="/about" title={t('about')} isActive={selectedIdx === 4} />
              </li>
              <li
                ref={(el) => {
                  itemRefs.current[5] = el
                }}
                className="relative z-10 flex"
                onMouseEnter={() => setHoveredIdx(5)}
                onMouseLeave={() => setHoveredIdx(null)}
              >
                <NavLink href="/news" title={t('news')} isActive={selectedIdx === 5} />
              </li>
              <li
                ref={(el) => {
                  itemRefs.current[6] = el
                }}
                className="relative z-10 flex"
                onMouseEnter={() => setHoveredIdx(6)}
                onMouseLeave={() => setHoveredIdx(null)}
              >
                <NavLink href="/kontakt" title={t('contact')} isActive={selectedIdx === 6} />
              </li>
            </ul>
            <LanguageToggle />
          </div>
        </div>
      </motion.nav>

      <nav className="mx-auto p-8 border border-accent rounded-3xl bg-KMRS-Darkmode fixed top-8 z-50 left-8 right-8 lg:hidden ">
        <div className="flex flex-row items-center justify-between">
          <div className="flex flex-row items-center">
            <Link href="/">
              <figure>
                <Image src={'/images/logos/kommers-logo.svg'} width={35} height={35} className="mr-5 aspect-square max-w-none object-cover" alt="KOMMERS Logo" />
              </figure>
            </Link>
            <div className="w-2 h-2 mr-2 rounded-full bg-accent" />
            <p className="text-KMRSx24-GreyLight font-light text-xs leading-3 line-clamp-6">
              Fürth, DE <TimeDisplay />
            </p>
          </div>
          <BurgerButton isOpen={isOpen} onClick={() => setIsOpen((prev) => !prev)} />
        </div>
        <motion.div
          className="h-0 overflow-hidden"
          animate={isOpen ? 'open' : 'closed'}
          variants={{
            open: { height: 'auto' }
          }}
        >
          <div className="w-full mt-8 border-t border-accent">
            <ul className="flex flex-col gap-y-8 py-8 lg:gap-y-16 lg:py-16 border-b border-accent">
              <MobileNavLink exact href="/" title={t('home')} />
              <MobileNavLink href="/ki" title={t('ai')} />
              <MobileNavLink href="/leistungen" title={t('services')} />
              <MobileNavLink href="/projekte" title={t('projects')} />
              <MobileNavLink href="/about" title={t('about')} />
              <MobileNavLink href="/news" title={t('news')} />
              <MobileNavLink href="/kontakt" title={t('contact')} />
            </ul>
            <div className="flex justify-center py-4 border-b border-accent">
              <LanguageToggle />
            </div>
            <div className="text-accent text-sm lg:text-lg flex flex-col mt-8">
              <a href="tel:+4991147734580">+49 (911) 477 345 80</a>
              <a href="mailto:hello@kommers.io">hello@kommers.io</a>
            </div>
          </div>
        </motion.div>
      </nav>
    </>
  )
}

export default NavBar
